import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["timingId", "actionId", "inactionId", "selectedAction"]
  static values = {
    transitionsData: Object,
    before: Number,
    after: Number,
    date: Number,
    orientationActionIds: Array,
    eventActionIds: Array,
    formActionIds: Array,
    surveyActionIds: Array,
    webtrainingActionIds: Array,
    milestonesActionIds: Array,
    mentoringActionIds: Array,
    formInactionIds: Array,
    surveyInactionIds: Array,
    webtrainingInactionIds: Array,
    eventInactionIds: Array,
    quizInactionIds: Array,
    milestoneInactionIds: Array
  }

  connect() {
    this.updateTriggerConditions()
  }

  // On submit
  clearInvisibleSelects() {
    this.element.querySelectorAll('.form-row').forEach(row => {
      if (row.style.display === 'none') {
        this.clearSelects(row)
        this.clearMultiselects(row)
        this.clearCheckboxes(row)
      }
    })
  }

  // Shows or hides a .form-row and clears its form fields when hiding
  // enabled: true = show row, false = hide row and clear fields
  toggleAndClear(element, enabled) {
    if (!element) return
    
    if (enabled) {
      element.style.display = ''
    } else {
      element.style.display = 'none'
      // Clear form fields when hiding
      this.clearSelects(element)
      this.clearCheckboxes(element)
      this.clearMultiselects(element)
    }
  }

  clearSelects(row) {
    row.querySelectorAll('select').forEach(s => s.value = '')
  }

  clearMultiselects(row) {
    const multiselectElements = row.querySelectorAll('[data-pc-section="inputmultiple"]')
    multiselectElements.forEach(multiselectElement => {
      if (multiselectElement) {
        multiselectElement.dispatchEvent(new CustomEvent('update:modelValue', {
          detail: [],
          bubbles: true
        }))
      }
    })
  }

  clearCheckboxes(row) {
    row.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = false)
  }

  // Called on page load and whenever timing, action, or inaction dropdowns change
  updateTriggerConditions() {
    // Cache all form rows once at the top since they are used multiple times
    const rows = {
      timingDetail:         document.querySelector('#trigger_timing_seconds')?.closest('.form-row'),
      sendAt:               document.querySelector('#trigger_send_at')?.closest('.form-row'),
      recipient:            document.querySelector('#trigger_recipient_group_ids')?.closest('.form-row'),
      thirdParty:           document.querySelector('#trigger_third_party_group_ids')?.closest('.form-row'),
      circle:               document.querySelector('#trigger_for_circles')?.closest('.form-row'),
      orientation:          document.querySelector('#trigger_orientation_response')?.closest('.form-row'),
      event:                document.querySelector('#trigger_event_responses')?.closest('.form-row'),
      form:                 document.querySelector('#trigger_form_ids')?.closest('.form-row'),
      survey:               document.querySelector('#trigger_survey_batch_ids')?.closest('.form-row'),
      webtraining:          document.querySelector('#trigger_webtraining_ids')?.closest('.form-row'),
      milestone:            document.querySelector('#trigger_milestone_ids')?.closest('.form-row'),
      mentoring:            document.querySelector('#trigger_mentoring_session_ids')?.closest('.form-row'),
      inactionForm:         document.querySelector('#trigger_inaction_form_ids')?.closest('.form-row'),
      inactionSurvey:       document.querySelector('#trigger_inaction_survey_batch_ids')?.closest('.form-row'),
      inactionWebtraining:  document.querySelector('#trigger_inaction_webtraining_ids')?.closest('.form-row'),
      inactionEvent:        document.querySelector('#trigger_inaction_event_ids')?.closest('.form-row'),
      inactionQuiz:         document.querySelector('#trigger_inaction_quiz_ids')?.closest('.form-row'),
      inactionMilestone:    document.querySelector('#trigger_inaction_milestone_ids')?.closest('.form-row')
    }

    // Handle timing conditions
    const timingId = this.timingIdTarget
    if (timingId && timingId.value) {
      const val = parseInt(timingId.value)

      // Toggle each timing row based on timing selection
      this.toggleAndClear(rows.timingDetail, (val === this.beforeValue || val === this.afterValue))
      this.toggleAndClear(rows.sendAt, (val === this.dateValue))
    }

    // Handle action conditions
    const actionSelect = this.actionIdTarget
    const actionId = actionSelect?.value

    const actionData = this.transitionsDataValue[actionId]

    if (actionId && actionId !== '') {
      const actionIdInt = parseInt(actionId)

      // Each row checks if THIS actionId should show THAT row type
      this.toggleAndClear(rows.recipient,   actionData.has_recipient)
      this.toggleAndClear(rows.thirdParty,  actionData.has_third_party)
      this.toggleAndClear(rows.circle,      actionData.has_circle)
      this.toggleAndClear(rows.orientation, this.orientationActionIdsValue.includes(actionIdInt))
      this.toggleAndClear(rows.event,       this.eventActionIdsValue.includes(actionIdInt))
      this.toggleAndClear(rows.form,        this.formActionIdsValue.includes(actionIdInt))
      this.toggleAndClear(rows.survey,      this.surveyActionIdsValue.includes(actionIdInt))
      this.toggleAndClear(rows.webtraining, this.webtrainingActionIdsValue.includes(actionIdInt))
      this.toggleAndClear(rows.milestone,   this.milestonesActionIdsValue.includes(actionIdInt))
      this.toggleAndClear(rows.mentoring,   this.mentoringActionIdsValue.includes(actionIdInt))

      this.selectedActionTarget.innerHTML = actionSelect.options[actionSelect.selectedIndex].text
    }

    // Handle inaction conditions
    const inactionId = this.inactionIdTarget?.value

    if (inactionId && inactionId !== '') {
      const inactionIdInt = parseInt(inactionId)

      // Toggle each inaction row based on inaction requirements
      this.toggleAndClear(rows.inactionForm,        this.formInactionIdsValue.includes(inactionIdInt))
      this.toggleAndClear(rows.inactionSurvey,      this.surveyInactionIdsValue.includes(inactionIdInt))
      this.toggleAndClear(rows.inactionWebtraining, this.webtrainingInactionIdsValue.includes(inactionIdInt))
      this.toggleAndClear(rows.inactionEvent,       this.eventInactionIdsValue?.includes(inactionIdInt))
      this.toggleAndClear(rows.inactionQuiz,        this.quizInactionIdsValue.includes(inactionIdInt))
      this.toggleAndClear(rows.inactionMilestone,   this.milestoneInactionIdsValue.includes(inactionIdInt))
    }
  }
}